import { useEffect, useMemo, useRef, useState } from 'react';
import { HeritageMap } from './components/Map';
import { LandmarkSidePanel } from './components/LandmarkSidePanel';
import { BusinessSidePanel } from './components/BusinessSidePanel';
import { AntwerpLandmarkPanel } from './components/AntwerpLandmarkPanel';
import { Attribution } from './components/Attribution';
import { AdminNotesPage } from './components/AdminNotesPage';
import { SearchBar } from './components/SearchBar';
import { LayerPanel } from './components/LayerPanel';
import { CategoryLegend } from './components/CategoryLegend';
import { TimeSlider } from './components/TimeSlider';
import { DarkModeToggle } from './components/DarkModeToggle';
import { MapContextMenu } from './components/MapContextMenu';
import { CustomPOIForm } from './components/CustomPOIForm';
import { PanelErrorBoundary } from './components/PanelErrorBoundary';
import { HistoricalMapsGallery } from './components/HistoricalMapsGallery';
import { SplatsGallery } from './components/SplatsGallery';
import {
  useLandmarkStore,
  parseHash,
  writeHashForLandmark,
  writeHashForBusiness,
  readAdminToken,
} from './state/landmarks';
import { useCatalogueData } from './lib/catalogue-loader';
import { loadAllCustomPOIImages } from './lib/custom-poi-images';
import businessesData from './data/businesses.json';
import placesData from './data/places.json';
import antwerpLandmarksData from './data/antwerp-landmarks.json';
import type { Landmark } from './types/landmark';
import type { Business, BusinessCatalogue } from './types/business';
import type { Place, PlaceCatalogue } from './types/place';
import type { AntwerpLandmark, AntwerpLandmarkCatalogue } from './types/antwerp-landmark';

const businessCatalogue = businessesData as BusinessCatalogue;
const businesses: Business[] = businessCatalogue.records;
const businessesBySlug = new Map(businesses.map((b) => [b.id, b]));

const placeCatalogue = placesData as PlaceCatalogue;
const places: Place[] = placeCatalogue.records;

const antwerpLandmarkCatalogue = antwerpLandmarksData as AntwerpLandmarkCatalogue;
const antwerpLandmarks: AntwerpLandmark[] = antwerpLandmarkCatalogue.records;

export default function App() {
  const [route, setRoute] = useState<{ kind: 'map' } | { kind: 'admin'; token: string | null }>(() =>
    parseHash().isAdmin ? { kind: 'admin', token: readAdminToken() } : { kind: 'map' },
  );

  useEffect(() => {
    const refreshRoute = () => {
      setRoute(parseHash().isAdmin ? { kind: 'admin', token: readAdminToken() } : { kind: 'map' });
    };
    window.addEventListener('hashchange', refreshRoute);
    return () => window.removeEventListener('hashchange', refreshRoute);
  }, []);

  if (route.kind === 'admin') {
    if (route.token === null || route.token.length === 0) {
      return (
        <div data-testid="admin-no-token" style={{ padding: 'var(--space-8)', maxWidth: 600 }}>
          <h1>Admin token required</h1>
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--text-muted)' }}>
            Append <code>?token=YOUR_TOKEN</code> to the URL to access the moderation page.
          </p>
        </div>
      );
    }
    return <AdminNotesPage token={route.token} />;
  }

  return (
    <PanelErrorBoundary label="App" fullScreen>
      <MapView />
    </PanelErrorBoundary>
  );
}

function MapView() {
  const data = useCatalogueData();

  const selectedLandmark = useLandmarkStore((state) => state.selectedLandmark);
  const selectedBusiness = useLandmarkStore((state) => state.selectedBusiness);
  const selectedAntwerpLandmark = useLandmarkStore((state) => state.selectedAntwerpLandmark);
  const selectLandmark = useLandmarkStore((state) => state.selectLandmark);
  const selectBusiness = useLandmarkStore((state) => state.selectBusiness);
  const selectAntwerpLandmark = useLandmarkStore((state) => state.selectAntwerpLandmark);
  const clearSelection = useLandmarkStore((state) => state.clearSelection);
  const activeTab = useLandmarkStore((state) => state.activeTab);
  const setActiveTab = useLandmarkStore((state) => state.setActiveTab);
  const flyTo = useLandmarkStore((state) => state.flyTo);
  const currentYear = useLandmarkStore((state) => state.currentYear);
  const setYear = useLandmarkStore((state) => state.setYear);
  const customPOIs = useLandmarkStore((state) => state.customPOIs);
  const setCustomPOIImages = useLandmarkStore((state) => state.setCustomPOIImages);
  const hydrateFromServer = useLandmarkStore((state) => state.hydrateFromServer);
  const refreshAdminFlag = useLandmarkStore((state) => state.refreshAdminFlag);
  const landmarkOverrides = useLandmarkStore((state) => state.landmarkOverrides);
  const [historicalMapsOpen, setHistoricalMapsOpen] = useState(false);
  const [splatsGalleryOpen, setSplatsGalleryOpen] = useState(false);

  // Boot sequence: hydrate the local IndexedDB image cache first (instant
  // render for stuff this browser saved earlier), check the Cloudflare Access
  // sign-in state, then pull authoritative state from the server.
  useEffect(() => {
    loadAllCustomPOIImages()
      .then((images) => setCustomPOIImages(images))
      .catch(() => undefined);
    void (async () => {
      await refreshAdminFlag();
      await hydrateFromServer();
    })();
  }, [setCustomPOIImages, hydrateFromServer, refreshAdminFlag]);

  // Apply admin edits on top of the static catalogue and custom POIs.
  // The side panel reads landmarkOverrides directly via its own subscription,
  // so it re-renders on edit without us having to re-fire selectAntwerpLandmark.
  const mergedAntwerpLandmarks: AntwerpLandmark[] = useMemo(() => {
    const all = [...antwerpLandmarks, ...Object.values(customPOIs)];
    return all.map((l) => {
      const ov = landmarkOverrides[l.id]?.fields;
      return ov ? ({ ...l, ...ov } as AntwerpLandmark) : l;
    });
  }, [customPOIs, landmarkOverrides]);

  const landmarks: Landmark[] = useMemo(() => data.catalogue?.records ?? [], [data.catalogue]);
  const landmarksBySlug = useMemo(() => new Map(landmarks.map((l) => [l.id, l])), [landmarks]);

  // Apply hash → state when data is ready and on hashchange.
  useEffect(() => {
    if (data.status !== 'ready') return;
    const applyHash = () => {
      const parsed = parseHash();
      if (parsed.isAdmin) return;
      if (parsed.year !== null) setYear(parsed.year);
      if (parsed.businessSlug !== null) {
        const b = businessesBySlug.get(parsed.businessSlug) ?? null;
        selectBusiness(b);
        if (b !== null) flyTo(b.coordinates);
        return;
      }
      if (parsed.landmarkSlug !== null) {
        const l = landmarksBySlug.get(parsed.landmarkSlug) ?? null;
        selectLandmark(l);
        if (l !== null) {
          setActiveTab(parsed.tab);
          flyTo(l.coordinates);
        }
        return;
      }
      if (parsed.year === null) clearSelection();
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [data.status, landmarksBySlug, selectLandmark, selectBusiness, setActiveTab, flyTo, clearSelection, setYear]);

  // Mirror state → hash. Skip until the catalogue has loaded AND the
  // parseHash effect has had at least one tick to apply the deep-link, so a
  // user landing on `#business/X` doesn't get the hash clobbered by an
  // initial null-selection mirror pass.
  const hasParsedHashOnce = useRef(false);
  useEffect(() => {
    if (data.status !== 'ready') return;
    if (!hasParsedHashOnce.current) {
      hasParsedHashOnce.current = true;
      return;
    }
    if (selectedBusiness !== null) {
      writeHashForBusiness(selectedBusiness.id, currentYear);
    } else {
      writeHashForLandmark(selectedLandmark?.id ?? null, activeTab, currentYear);
    }
  }, [data.status, selectedLandmark, selectedBusiness, activeTab, currentYear]);

  if (data.status === 'loading') {
    return <LoadingScreen />;
  }
  if (data.status === 'error' || !data.catalogue || !data.zone) {
    return <ErrorScreen message={data.error ?? 'unknown'} />;
  }

  return (
    <div data-testid="app-root" style={{ position: 'relative', height: '100vh', width: '100vw' }}>
      <HeritageMap
        landmarks={landmarks}
        businesses={businesses}
        antwerpLandmarks={mergedAntwerpLandmarks}
        zone={data.zone}
      />
      <TimeSlider />
      <SearchBar
        landmarks={landmarks}
        businesses={businesses}
        places={places}
        onSelect={(result) => {
          if (result.kind === 'landmark') {
            selectLandmark(result.record as Landmark);
            flyTo(result.coordinates);
          } else if (result.kind === 'business') {
            selectBusiness(result.record as Business);
            flyTo(result.coordinates);
          } else {
            // Place: just pan/zoom — no side panel.
            clearSelection();
            flyTo(result.coordinates, result.flyZoom);
          }
        }}
      />
      <LandmarkSidePanel />
      <BusinessSidePanel />
      {selectedAntwerpLandmark ? (
        <PanelErrorBoundary label="Landmark panel">
          <AntwerpLandmarkPanel
            landmark={selectedAntwerpLandmark}
            onClose={() => selectAntwerpLandmark(null)}
          />
        </PanelErrorBoundary>
      ) : null}
      <LayerPanel
        onOpenHistoricalMaps={() => setHistoricalMapsOpen(true)}
        onOpenSplatsGallery={() => setSplatsGalleryOpen(true)}
      />
      <CategoryLegend antwerpLandmarks={mergedAntwerpLandmarks} />
      <DarkModeToggle />
      <Attribution />
      <MapContextMenu />
      <CustomPOIForm />
      <HistoricalMapsGallery
        open={historicalMapsOpen}
        onClose={() => setHistoricalMapsOpen(false)}
      />
      <SplatsGallery
        isOpen={splatsGalleryOpen}
        onClose={() => setSplatsGalleryOpen(false)}
        records={data.catalogue?.records ?? []}
      />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div
      data-testid="app-loading"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        background: 'var(--background)',
        color: 'var(--text-muted)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          border: '3px solid var(--border)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <p style={{ fontFamily: 'var(--font-serif)' }}>Loading Flanders heritage data…</p>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="app-loading"] div[aria-hidden] { animation-duration: 4s; }
        }
      `}</style>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div
      data-testid="app-error"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        background: 'var(--background)',
        color: 'var(--text)',
        padding: 'var(--space-6)',
        textAlign: 'center',
      }}
    >
      <h1>Couldn’t load the map data</h1>
      <p style={{ color: 'var(--text-muted)', maxWidth: 480 }}>
        We couldn’t fetch the catalogue. {message}. Refresh the page or check your network.
      </p>
    </div>
  );
}
